import React from 'react';

import { Checkbox } from '@mantine/core';
import type { ColumnDef } from '@tanstack/react-table';

import { ChainSequenceCell } from '../../antibody-analysis/components/InnerSequenceTable/ChainSequenceCell';
import { LiabilityBadgeCell } from '../../antibody-analysis/components/InnerSequenceTable/LiabilityBadgeCell';
import { NodeCell } from '../../antibody-analysis/components/InnerSequenceTable/NodeCell';
import { LiabilityColumnHeader } from '../../antibody-analysis/components/common/LiabilityColumnHeader';
import { highlightText } from '../../antibody-analysis/utilities/highlightText';
import type { ExportRow } from '../types';
import { formatExportNums } from '../utilities/exportSummaryUtils';

const s = {
  cellText: 'text-xs block',
} as const;

// Matches the size/colour the Hit selection select columns use.
const CHECKBOX_COLOR = '#0064B0';

/**
 * Fixed (non-chain) column widths, keyed by column id — the single place these numbers live.
 */
const COLUMN_SIZES: Record<string, number> = {
  select: 36,
  exportNums: 80,
  clonotypeId: 100,
  node: 90,
  vjGeneHeavy: 140,
  vjGeneLight: 140,
  cdrh3: 170,
  cdrl3: 170,
  nodeSize: 90,
  aps: 80,
  liabilities: 110,
  shms: 80,
} as const;

export interface ExportTableColumnsParams {
  isSelected: (nodeId: number) => boolean;
  onToggle: (nodeId: number) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  chainType: 'all' | 'heavy' | 'light';
  searchQuery: string;
  showFWR: boolean;
  highlightedRegions: string[];
  showLiabilities: boolean;
  showShms: boolean;
  heavyChainWidth: number;
  lightChainWidth: number;
  onRegionClick: (region: string | null) => void;
}

/**
 * Column definitions for the flat export table.
 *
 * Hit selection splits these across two nested tables — clonotype-level fields in
 * `OuterClonotypeTableColumns`, node-level fields in `InnerChildSequenceTableColumns`. The
 * export table is flat (one row per node), so both sets live here against a single row type.
 * The cell renderers are the shared ones, so sequence tinting, SHM markers and liability
 * badges look identical to Hit selection.
 *
 * Deliberately absent: the pencil / in-place edit column and the editable status chips.
 */
export function ExportTableColumns({
  isSelected,
  onToggle,
  onToggleAll,
  allSelected,
  someSelected,
  chainType,
  searchQuery,
  showFWR,
  highlightedRegions,
  showLiabilities,
  showShms,
  heavyChainWidth,
  lightChainWidth,
  onRegionClick,
}: ExportTableColumnsParams): ColumnDef<ExportRow>[] {
  const columns: ColumnDef<ExportRow>[] = [];

  columns.push({
    id: 'select',
    header: () => (
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        onChange={onToggleAll}
        size="xs"
        color={CHECKBOX_COLOR}
        aria-label="Select all sequences"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={isSelected(row.original.nodeId)}
        onChange={() => onToggle(row.original.nodeId)}
        onClick={(event) => event.stopPropagation()}
        size="xs"
        color={CHECKBOX_COLOR}
        aria-label={`Select ${row.original.nodeLabel}`}
      />
    ),
    enableSorting: false,
  });

  columns.push(
    {
      id: 'exportNums',
      header: 'Export',
      accessorFn: (row) => formatExportNums(row.exportNums),
      cell: ({ row }) => <span className={s.cellText}>{formatExportNums(row.original.exportNums)}</span>,
    },
    {
      id: 'clonotypeId',
      header: 'Clonotype ID',
      accessorKey: 'clonotypeId',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(String(row.original.clonotypeId), searchQuery)}</span>,
    },
    {
      id: 'node',
      header: 'Node',
      accessorKey: 'nodeLabel',
      cell: ({ row }) => <NodeCell nodeLabel={row.original.nodeLabel} barcodes={row.original.sequenceData.metadata.barcodes} searchQuery={searchQuery} />,
    },
    {
      id: 'vjGeneHeavy',
      header: 'V–J gene (heavy)',
      accessorKey: 'vjGeneHeavy',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.vjGeneHeavy, searchQuery)}</span>,
    },
    {
      id: 'vjGeneLight',
      header: 'V–J gene (light)',
      accessorKey: 'vjGeneLight',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.vjGeneLight, searchQuery)}</span>,
    },
    {
      id: 'cdrh3',
      header: 'CDRH3',
      accessorKey: 'cdrh3',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.cdrh3, searchQuery)}</span>,
    },
    {
      id: 'cdrl3',
      header: 'CDRL3',
      accessorKey: 'cdrl3',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.cdrl3, searchQuery)}</span>,
    },
    {
      id: 'nodeSize',
      header: 'Node size',
      accessorKey: 'nodeSize',
      cell: ({ row }) => <span className={s.cellText}>{row.original.nodeSize}</span>,
    },
    {
      id: 'aps',
      header: 'APS',
      accessorKey: 'aps',
      cell: ({ row }) => <span className={s.cellText}>{row.original.aps?.toFixed(2) ?? '—'}</span>,
    },
    {
      id: 'liabilities',
      header: () => <LiabilityColumnHeader />,
      accessorKey: 'liabilityScore',
      cell: ({ row }) => <LiabilityBadgeCell sequenceData={row.original.sequenceData} searchQuery={searchQuery} />,
    },
    {
      id: 'shms',
      header: 'SHMs',
      accessorKey: 'shms',
      cell: ({ row }) => <span className={s.cellText}>{row.original.shms ?? '—'}</span>,
    },
  );

  if (chainType !== 'light') {
    columns.push({
      id: 'heavyChain',
      header: 'Heavy Chains (IGH)',
      cell: ({ row }) => (
        <ChainSequenceCell
          sequence={row.original.sequenceData}
          chainKey="heavy"
          showFWR={showFWR}
          highlightedRegions={highlightedRegions}
          searchQuery={searchQuery}
          showLiabilities={showLiabilities}
          showShms={showShms}
          onRegionClick={onRegionClick}
          regionNumbers={row.original.parent.heavyRegions}
        />
      ),
      size: heavyChainWidth,
      enableSorting: false,
    });
  }

  if (chainType !== 'heavy') {
    columns.push({
      id: 'lightChain',
      header: 'Light Chains (IGL)',
      cell: ({ row }) => (
        <ChainSequenceCell
          sequence={row.original.sequenceData}
          chainKey="light"
          showFWR={showFWR}
          highlightedRegions={highlightedRegions}
          searchQuery={searchQuery}
          showLiabilities={showLiabilities}
          showShms={showShms}
          onRegionClick={onRegionClick}
          regionNumbers={row.original.parent.lightRegions ?? undefined}
        />
      ),
      size: lightChainWidth,
      enableSorting: false,
    });
  }

  // Single application point for the fixed widths; dynamic chain widths are already set.
  return columns.map((column) => (column.id && COLUMN_SIZES[column.id] !== undefined ? { ...column, size: COLUMN_SIZES[column.id] } : column));
}
